import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { attachmentsApi, customersApi, dairyCollectionHubsApi, hubPaymentsApi } from '../../services/resources';
import { getApiErrorMessage } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { formatCurrency, formatDate, formatNumber, today } from '../../utils/format';
import { ILMSAlert } from '../../components/ILMSAlert';
import { ILMSButton } from '../../components/ILMSButton';
import { ILMSModal } from '../../components/ILMSModal';
import { ILMSTable, type ILMSTableColumn } from '../../components/ILMSTable';
import { ILMSAttachmentPreview } from '../../components/ILMSAttachmentPreview';
import type { HubPayment } from '../../types';

// The Dairy Collection Hub pays for its milk twice a month (1st-15th and
// 16th-month end), often into a partner's account first. Recording that
// payment here marks the period's Approved Hub sales "Paid by hub" and sets
// what they came to against what was actually received
// (app/api/v1/dairy.py, /hub-payments).

const inputClass = 'h-11 w-full rounded-md border border-ink-300/50 bg-white px-3 text-body focus-visible:outline-2 focus-visible:outline-brand-green-500';
const labelClass = 'flex flex-col gap-1 text-label text-ink-700';

const pad = (n: number) => String(n).padStart(2, '0');
const lastDay = (month: string) => {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m, 0).getDate();
};
const halfOf = (month: string, half: 1 | 2) => (half === 1
  ? { from: `${month}-01`, to: `${month}-15` }
  : { from: `${month}-16`, to: `${month}-${pad(lastDay(month))}` });

/** The last half-month that has ended: before the 16th, last month's 16-end. */
function lastClosedHalf() {
  const t = today();
  const month = t.slice(0, 7);
  if (Number(t.slice(8, 10)) > 15) return { month, half: 1 as const };
  const [y, m] = month.split('-').map(Number);
  const prev = new Date(y, m - 2, 1);
  return { month: `${prev.getFullYear()}-${pad(prev.getMonth() + 1)}`, half: 2 as const };
}

const periodLabel = (p: { period_from: string; period_to: string }) => `${formatDate(p.period_from, 'dd MMM yyyy')} – ${formatDate(p.period_to, 'dd MMM yyyy')}`;

export function HubPaymentsTab() {
  const { can } = useAuth();
  const [editing, setEditing] = React.useState<HubPayment | 'new' | null>(null);
  const list = useQuery({ queryKey: ['hub-payments'], queryFn: () => hubPaymentsApi.list({ page_size: 200 }) });
  const hubs = useQuery({ queryKey: ['dairy-collection-hubs-default'], queryFn: () => dairyCollectionHubsApi.list({ page_size: 200 }) });
  const parties = useQuery({ queryKey: ['customers-full-for-milk-sales'], queryFn: () => customersApi.list({ page_size: 500 }) });
  const hubName = (id: string) => hubs.data?.items.find((h) => h.id === id)?.name ?? '—';
  const partyName = (id?: string | null) => (id ? parties.data?.items.find((c) => c.id === id)?.name ?? '—' : '');

  const rows = list.data?.items ?? [];
  const totals = rows.reduce((t, r) => ({ expected: t.expected + Number(r.expected_amount), received: t.received + Number(r.amount_received) }), { expected: 0, received: 0 });

  const columns: ILMSTableColumn<HubPayment>[] = [
    { key: 'period', header: 'Period', primary: true, render: (r) => (
      <span>{periodLabel(r)}<span className="ml-1.5 text-helper-text text-ink-400">{r.payment_code}</span></span>
    ) },
    { key: 'hub', header: 'Hub', render: (r) => hubName(r.dairy_collection_hub_id) },
    { key: 'litres', header: 'Litres', align: 'right', render: (r) => `${formatNumber(r.litres, 2)} · ${r.sale_count} slips` },
    { key: 'expected', header: 'Milk value', align: 'right', render: (r) => formatCurrency(r.expected_amount) },
    { key: 'received', header: 'Received', align: 'right', render: (r) => formatCurrency(r.amount_received) },
    { key: 'diff', header: 'Difference', align: 'right', render: (r) => {
      const d = Number(r.amount_received) - Number(r.expected_amount);
      if (Math.abs(d) < 0.005) return <span className="text-success-700">Matches</span>;
      return <span className={d < 0 ? 'text-critical-700 font-medium' : 'text-info-700'}>{d < 0 ? '−' : '+'}{formatCurrency(Math.abs(d))}</span>;
    } },
    { key: 'on', header: 'Received On', render: (r) => formatDate(r.received_on) },
    { key: 'via', header: 'Via', hideOnTablet: true, render: (r) => (r.via_customer_id ? `Partner: ${partyName(r.via_customer_id)}` : 'Direct') },
    { key: 'ref', header: 'Reference', hideOnTablet: true, render: (r) => r.reference || '—' },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-page-title">Hub Payments / ಡೈರಿ ಪಾವತಿಗಳು</h2>
          <p className="text-secondary text-ink-500">
            What the Dairy Collection Hub paid for each period (1–15, 16–month end). Saving one marks that period's approved Hub sales as paid.
          </p>
        </div>
        {can('Sales', 'Create') && <ILMSButton onClick={() => setEditing('new')}>+ Record Hub Payment</ILMSButton>}
      </div>
      {rows.length > 0 && (
        <p className="text-secondary text-ink-600">
          Listed: milk value {formatCurrency(totals.expected)} · received {formatCurrency(totals.received)}
          {Math.abs(totals.received - totals.expected) >= 0.005 && ` · difference ${formatCurrency(totals.received - totals.expected)}`}
        </p>
      )}
      <ILMSTable
        columns={columns} data={rows} rowKey={(r) => r.id} exportTitle="Hub Payments"
        isLoading={list.isLoading}
        error={list.isError ? getApiErrorMessage(list.error, 'Unable to load hub payments.') : null}
        onRetry={() => list.refetch()}
        onRowClick={can('Sales', 'Edit') ? setEditing : undefined}
        emptyTitle="No hub payments recorded yet."
      />
      {editing && <HubPaymentModal payment={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function HubPaymentModal({ payment, onClose }: { payment: HubPayment | null; onClose: () => void }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const hubs = useQuery({ queryKey: ['dairy-collection-hubs-default'], queryFn: () => dairyCollectionHubsApi.list({ page_size: 200 }) });
  const parties = useQuery({ queryKey: ['customers-full-for-milk-sales'], queryFn: () => customersApi.list({ page_size: 500 }) });
  const partners = (parties.data?.items ?? []).filter((c) => c.party_type === 'Partner');
  const hubList = (hubs.data?.items ?? []).filter((h) => h.active || h.id === payment?.dairy_collection_hub_id);

  const start = lastClosedHalf();
  const [hubId, setHubId] = React.useState(payment?.dairy_collection_hub_id ?? '');
  const [from, setFrom] = React.useState(payment?.period_from ?? halfOf(start.month, start.half).from);
  const [to, setTo] = React.useState(payment?.period_to ?? halfOf(start.month, start.half).to);
  const [receivedOn, setReceivedOn] = React.useState(payment?.received_on ?? today());
  const [amount, setAmount] = React.useState(payment ? String(Number(payment.amount_received)) : '');
  const [via, setVia] = React.useState(payment?.via_customer_id ?? '');
  const [reference, setReference] = React.useState(payment?.reference ?? '');
  const [notes, setNotes] = React.useState(payment?.notes ?? '');
  const [photo, setPhoto] = React.useState<File | null>(null);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const month = from.slice(0, 7);

  // The default hub, once the list has loaded.
  React.useEffect(() => {
    if (!hubId && hubList.length) setHubId((hubList.find((h) => h.is_default) ?? hubList[0]).id);
  }, [hubId, hubList]);

  const preview = useQuery({
    queryKey: ['hub-payments', 'preview', hubId, from, to, payment?.id],
    queryFn: () => hubPaymentsApi.preview({ dairy_collection_hub_id: hubId, period_from: from, period_to: to, payment_id: payment?.id }),
    enabled: !!hubId && !!from && !!to && from <= to,
  });
  const p = preview.data;
  const expected = Number(p?.expected_amount ?? 0);
  const diff = Number(amount || 0) - expected;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['hub-payments'] });
    queryClient.invalidateQueries({ queryKey: ['milk-sales'] });
  };
  const save = useMutation({
    mutationFn: async () => {
      const body = {
        dairy_collection_hub_id: hubId, period_from: from, period_to: to, received_on: receivedOn,
        amount_received: Number(amount), via_customer_id: via || null, reference: reference.trim() || null, notes: notes.trim() || null,
      } as unknown as Partial<HubPayment>;
      let saved = payment ? await hubPaymentsApi.update(payment.id, body) : await hubPaymentsApi.create(body);
      if (photo) {
        try {
          const att = await attachmentsApi.upload(photo, 'HubPayment', saved.id);
          saved = await hubPaymentsApi.update(saved.id, { proof_attachment_id: att.id } as Partial<HubPayment>);
        } catch (err) {
          refresh();
          throw new Error(getApiErrorMessage(err, 'The payment was saved, but the photo failed to upload — open it and try again.'));
        }
      }
      return saved;
    },
    onSuccess: () => { refresh(); onClose(); },
  });
  const remove = useMutation({
    mutationFn: () => hubPaymentsApi.remove!(payment!.id),
    onSuccess: () => { refresh(); onClose(); },
  });
  const valid = !!hubId && from <= to && !!receivedOn && amount !== '' && Number(amount) >= 0;

  const halfButton = (half: 1 | 2, label: string) => {
    const h = halfOf(month, half);
    const on = from === h.from && to === h.to;
    return (
      <button type="button" onClick={() => { setFrom(h.from); setTo(h.to); }}
        className={`rounded-full border px-3 py-1.5 text-secondary font-medium ${on ? 'border-brand-green-700 bg-brand-green-700 text-white' : 'border-ink-300/50 text-ink-700 hover:border-brand-green-400'}`}>
        {label}
      </button>
    );
  };

  return (
    <ILMSModal
      open onClose={onClose} size="lg"
      title={payment ? `Hub payment ${payment.payment_code ?? ''}` : 'Record hub payment'}
      footer={(
        <>
          {payment && can('Sales', 'Delete/Archive') && (
            <span className="mr-auto">
              {confirmDelete ? (
                <span className="flex items-center gap-2 text-secondary">
                  Delete? Its sales go back to unpaid.
                  <ILMSButton size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>No</ILMSButton>
                  <ILMSButton size="sm" variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>Yes, delete</ILMSButton>
                </span>
              ) : <ILMSButton size="sm" variant="danger" onClick={() => setConfirmDelete(true)}>Delete</ILMSButton>}
            </span>
          )}
          <ILMSButton variant="ghost" onClick={onClose}>Cancel</ILMSButton>
          <ILMSButton disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>{payment ? 'Save changes' : 'Save payment'}</ILMSButton>
        </>
      )}
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className={labelClass}>
            Dairy Collection Hub *
            <select className={inputClass} value={hubId} onChange={(e) => setHubId(e.target.value)}>
              <option value="">Select hub…</option>
              {hubList.map((h) => <option key={h.id} value={h.id}>{h.hub_code ? `${h.hub_code} — ` : ''}{h.name}</option>)}
            </select>
          </label>
          <label className={labelClass}>
            Month
            <input type="month" className={inputClass} value={month} max={today().slice(0, 7)}
              onChange={(e) => {
                if (!e.target.value) return;
                const half = Number(from.slice(8, 10)) > 15 ? 2 : 1;
                const h = halfOf(e.target.value, half);
                setFrom(h.from); setTo(h.to);
              }} />
          </label>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-label text-ink-700">Period paid for *</span>
          <div className="flex flex-wrap items-center gap-1.5">
            {halfButton(1, '1st – 15th')}
            {halfButton(2, `16th – ${lastDay(month)}${lastDay(month) === 31 ? 'st' : 'th'}`)}
            <span className="text-helper-text text-ink-500">or pick the days:</span>
            <input type="date" aria-label="From" className={`${inputClass} !w-auto`} value={from} max={today()} onChange={(e) => setFrom(e.target.value)} />
            <span className="text-ink-500">to</span>
            <input type="date" aria-label="To" className={`${inputClass} !w-auto`} value={to} min={from} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>

        <div className="rounded-md border border-ink-300/30 bg-surface-subtle p-3">
          {from > to ? <p className="text-secondary text-critical-700">The end date is before the start.</p>
            : preview.isLoading || !p ? <p className="text-secondary text-ink-500">{hubId ? 'Adding up the Hub sales…' : 'Pick the hub.'}</p>
              : (
                <div className="flex flex-col gap-2">
                  <p className="text-body">
                    <b>{p.sale_count}</b> approved Hub slip{p.sale_count === 1 ? '' : 's'} · <b>{formatNumber(p.litres, 2)} L</b> · milk value <b>{formatCurrency(p.expected_amount)}</b>
                  </p>
                  {p.pending_count > 0 && (
                    <ILMSAlert tone="warning">
                      {p.pending_count} slip{p.pending_count === 1 ? '' : 's'} ({formatNumber(p.pending_litres, 2)} L) in this period still wait for approval and are not counted yet — once approved they are added to this payment by themselves.
                    </ILMSAlert>
                  )}
                  {p.already_paid_count > 0 && (
                    <p className="text-helper-text text-ink-500">{p.already_paid_count} slip{p.already_paid_count === 1 ? ' is' : 's are'} already paid through another hub payment — not counted again.</p>
                  )}
                  {p.days.length > 0 && (
                    <details>
                      <summary className="cursor-pointer text-secondary text-brand-green-700">Day by day</summary>
                      <div className="mt-2 grid grid-cols-[auto_auto_1fr_auto] gap-x-4 gap-y-0.5 text-secondary tabular-nums">
                        {p.days.map((d) => (
                          <React.Fragment key={d.date}>
                            <span>{formatDate(d.date, 'dd MMM yyyy')}</span>
                            <span className="text-ink-500">{d.shifts.map((s) => (s === 'Morning' ? 'AM' : s === 'Evening' ? 'PM' : s)).join(' + ')}</span>
                            <span className="text-right">{formatNumber(d.litres, 2)} L</span>
                            <span className="text-right">{formatCurrency(d.amount)}</span>
                          </React.Fragment>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className={labelClass}>
            Amount received (₹) *
            <input type="number" min={0} step="0.01" className={inputClass} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="As credited" />
            {p && expected > 0 && amount === '' && (
              <button type="button" className="self-start text-helper-text font-medium text-brand-green-700" onClick={() => setAmount(expected.toFixed(2))}>
                Same as milk value ({formatCurrency(expected)})
              </button>
            )}
            {p && amount !== '' && Math.abs(diff) >= 0.005 && (
              <span className={`text-helper-text font-normal ${diff < 0 ? 'text-critical-700' : 'text-info-700'}`}>
                {diff < 0 ? `${formatCurrency(-diff)} less than the milk value` : `${formatCurrency(diff)} more than the milk value`} — say why in Notes (deductions, fat rate…).
              </span>
            )}
          </label>
          <label className={labelClass}>
            Received on *
            <input type="date" className={inputClass} value={receivedOn} max={today()} onChange={(e) => setReceivedOn(e.target.value)} />
          </label>
          <label className={labelClass}>
            Paid into <span className="text-helper-text font-normal text-ink-400">(optional)</span>
            <select className={inputClass} value={via} onChange={(e) => setVia(e.target.value)}>
              <option value="">Company account directly</option>
              {partners.map((c) => <option key={c.id} value={c.id}>Partner's account: {c.name}</option>)}
            </select>
            <span className="text-helper-text font-normal text-ink-500">When the hub pays a partner, who then transfers it to the company.</span>
          </label>
          <label className={labelClass}>
            Reference <span className="text-helper-text font-normal text-ink-400">(optional)</span>
            <input className={inputClass} value={reference} onChange={(e) => setReference(e.target.value)} placeholder="UTR / UPI ref / cheque no." />
          </label>
          <label className={`${labelClass} sm:col-span-2`}>
            Notes <span className="text-helper-text font-normal text-ink-400">(optional)</span>
            <input className={inputClass} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. ₹50 deducted for cans" />
          </label>
          <div className={`${labelClass} sm:col-span-2`}>
            Proof <span className="text-helper-text font-normal text-ink-400">(optional — bank / UPI screenshot, hub statement)</span>
            {!photo && payment?.proof_attachment_id && <ILMSAttachmentPreview attachmentId={payment.proof_attachment_id} label="Current proof" />}
            <input type="file" accept="image/*,application/pdf" onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
              className="text-body text-ink-700 file:mr-3 file:rounded-md file:border-0 file:bg-brand-green-50 file:px-3 file:py-2 file:text-label file:text-brand-green-800" />
          </div>
        </div>
        {(save.isError || remove.isError) && (
          <ILMSAlert tone="critical">{getApiErrorMessage(save.error ?? remove.error, 'Unable to save this hub payment.')}</ILMSAlert>
        )}
      </div>
    </ILMSModal>
  );
}
